import {
  RemoteData,
  foldRemoteData,
} from "@nvdb-rapport/shared/src/utils/remoteData";
import {
  KostraRapportTypeList,
  KostraRapportType,
  PollData,
} from "../../data/api";
import { Maybe, Nothing, Either, Right } from "purify-ts";
import * as R from "remeda";
import {
  Field,
  initialField,
} from "@nvdb-rapport/shared/src/components/formTypes";
import { VegSystem } from "@nvdb-rapport/shared/src/utils/vegfilter";
import {
  Fylke,
  Kommune,
  Omraader,
} from "@nvdb-rapport/shared/src/components/AreaSelect/types";

import { ErrorData } from "@nvdb-rapport/shared/src/components/ErrorBox";
import { ReadVersionOption } from "./ReadVersionSelect";

// Component Props
export type ModelProp = { model: FormModel };
export type DispatchProp = { dispatch: React.Dispatch<FormAction> };

// Model
export interface FormModel {
  appStatus: AppStatus;
  omraader: RemoteData<Error, Omraader>;
  kostraTyper: RemoteData<Error, KostraRapportTypeList>;
  selectedRapportType: Field<KostraRapportType | undefined>;
  selectedFylker: Field<Fylke[]>;
  selectedKommuner: Field<Kommune[]>;
  reportData: Maybe<PollData>;
  vegsystemInput: string;
  vegsystem: Either<string, VegSystem>;
  errors: ErrorData[];
  selectedReadVersion: ReadVersionOption | null;
}

export const initialFormModel: FormModel = {
  appStatus: { type: "Initial" },
  omraader: { type: "NOT_ASKED" },
  kostraTyper: { type: "NOT_ASKED" },
  selectedFylker: initialField([]),
  selectedKommuner: initialField([]),
  selectedRapportType: initialField(undefined),
  reportData: Nothing,
  vegsystemInput: "",
  vegsystem: Right([]),
  errors: [],
  selectedReadVersion: { label: "v4", value: true },
};

type AppStatus =
  | { type: "Initial" }
  | { type: "Ready" }
  | { type: "FetchingReport" }
  | { type: "FetchedReport" }
  | { type: "Error" };

// TODO: Make common

type SetOmraader = {
  type: "SetOmraader";
  payload: RemoteData<Error, Omraader>;
};
export function setOmraaderAction(
  data: RemoteData<Error, Omraader>
): SetOmraader {
  return {
    type: "SetOmraader",
    payload: data,
  };
}

type SetKostraTyper = {
  type: "SetKostraTyper";
  payload: RemoteData<Error, KostraRapportTypeList>;
};
export function setKostratypeAction(
  data: RemoteData<Error, KostraRapportTypeList>
): SetKostraTyper {
  return {
    type: "SetKostraTyper",
    payload: data,
  };
}

type SelectKostraRapportType = {
  type: "SelectKostraRapportType";
  payload: Field<KostraRapportType | undefined>;
};
export function selectKostraRapportTypeAction(
  type: Field<KostraRapportType | undefined>
): SelectKostraRapportType {
  return {
    type: "SelectKostraRapportType",
    payload: type,
  };
}

type ToggleSelectedFylke = {
  type: "ToggleSelectedFylke";
  payload: Field<Fylke[]>;
};
export function toggleSelectedFylkeAction(
  fylke: Field<Fylke[]>
): ToggleSelectedFylke {
  return {
    type: "ToggleSelectedFylke",
    payload: fylke,
  };
}

type UpdateSelectedKommune = {
  type: "UpdateSelectedKommune";
  payload: Field<Fylke[]>;
};
export function updateSelectedKommuneAction(
  kommune: Field<Kommune[]>
): UpdateSelectedKommune {
  return {
    type: "UpdateSelectedKommune",
    payload: kommune,
  };
}

type SetAppStatus = { type: "SetAppStatus"; payload: AppStatus };
export function setAppStatus(status: AppStatus): SetAppStatus {
  return {
    type: "SetAppStatus",
    payload: status,
  };
}

type SetReport = { type: "SetReport"; payload: Maybe<PollData> };
export function setReportAction(maybePollData: Maybe<PollData>): SetReport {
  return {
    type: "SetReport",
    payload: maybePollData,
  };
}

type AddError = { type: "AddError"; payload: ErrorData };

export function addErrorAction(errorData: ErrorData): AddError {
  return { type: "AddError", payload: errorData };
}

type UpdateVegsystem = {
  type: "UpdateVegsystem";
  payload: string;
};

export function updateVegsystemAction(input: string): UpdateVegsystem {
  return {
    type: "UpdateVegsystem",
    payload: input,
  };
}

type ResetForm = {
  type: "ResetForm";
};

export function resetFormAction(): ResetForm {
  return {
    type: "ResetForm",
  };
}

type SetReadVersion = {
  type: "SetReadVersion";
  payload: ReadVersionOption | null;
};
export function selectReadVersion(
  type: ReadVersionOption | null
): SetReadVersion {
  return {
    type: "SetReadVersion",
    payload: type,
  };
}

// MESSAGE

export type FormAction =
  | SetOmraader
  | SetKostraTyper
  | ToggleSelectedFylke
  | SelectKostraRapportType
  | SetReport
  | SetAppStatus
  | AddError
  | UpdateVegsystem
  | UpdateSelectedKommune
  | ResetForm
  | SetReadVersion;

export function toggleSelectedFylke(
  omraader: RemoteData<Error, Omraader>,
  selectedFylker: Fylke[],
  selectedFylke: Fylke
): Fylke[] {
  return foldRemoteData(
    omraader,
    () => [],
    () => [],
    () => [],
    (/* omraadeData */) => {
      const fylkeSearch = R.find(selectedFylker, f => f === selectedFylke);
      if (fylkeSearch === undefined) {
        return R.pipe(
          selectedFylker,
          R.concat([selectedFylke]),
          R.uniqBy(f => f.nummer)
        );
      } else {
        return R.pipe(
          selectedFylker,
          R.filter(f => f === fylkeSearch),
          R.uniqBy(f => f.nummer)
        );
      }
    }
  );
}

export function formModelReducer(
  model: FormModel,
  action: FormAction
): FormModel {
  switch (action.type) {
    case "SetOmraader":
      return R.pipe(model, R.set("omraader", action.payload));
    case "SelectKostraRapportType":
      return R.pipe(model, R.set("selectedRapportType", action.payload));
    case "SetAppStatus":
      return R.pipe(model, R.set("appStatus", action.payload));
    case "SetKostraTyper":
      return R.pipe(model, R.set("kostraTyper", action.payload));
    case "ToggleSelectedFylke":
      return R.pipe(model, R.set("selectedFylker", action.payload));
    case "UpdateSelectedKommune":
      return R.pipe(model, R.set("selectedKommuner", action.payload));
    case "SetReport":
      return R.pipe(
        model,
        R.set("reportData", action.payload),
        R.set("errors", [])
      );
    case "ResetForm":
      return R.pipe(
        initialFormModel,
        R.set("omraader", model.omraader),
        R.set("kostraTyper", model.kostraTyper),
        R.set("appStatus", { type: "Initial" })
      );
    case "AddError":
      return R.pipe(
        model,
        R.set("errors", R.concat(model.errors, [action.payload])),
        R.set("appStatus", { type: "Error" })
      );
    case "UpdateVegsystem":
      return {
        ...model,
        vegsystemInput: action.payload,
        vegsystem: VegSystem.decode(action.payload.split(",")),
      };
    case "SetReadVersion":
      return R.pipe(model, R.set("selectedReadVersion", action.payload));
  }
}
